import { createTheme } from '@mui/material/styles';

export const cleanTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#F24F2F',
      light: '#f5795d',
      dark: '#d94429',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9c27b0',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          color: '#000000',
          borderRadius: '16px',
          border: '1px solid #e0e0e0',
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#000000',
          fontWeight: 600,
          fontSize: '1.25rem',
          fontFamily: "'ClashGrotesk', sans-serif",
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: '#000000',
          '& .MuiTypography-root': {
            color: '#000000'
          },
          '& .MuiTypography-subtitle2': {
            color: '#666666',
            fontWeight: 600
          },
          '& .MuiTypography-body2': {
            color: '#000000'
          },
          '& .MuiTypography-caption': {
            color: '#666666'
          }
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '& .MuiButton-root': {
            color: '#000000',
            backgroundColor: 'rgba(79, 195, 247, 0.1)',
            border: '1px solid rgba(79, 195, 247, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(79, 195, 247, 0.2)',
              color: '#000000'
            }
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          border: '1px solid #e0e0e0',
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#000000',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            color: '#000000'
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(242, 79, 47, 0.12)',
            color: '#000000',
            '&:hover': {
              backgroundColor: 'rgba(242, 79, 47, 0.2)',
              color: '#000000'
            }
          }
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          border: '1px solid #e0e0e0',
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            color: '#000000',
            '& input': {
              color: '#000000'
            },
            '& fieldset': {
              borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
              borderColor: '#bdbdbd',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#F24F2F',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#666666',
            '&.Mui-focused': {
              color: '#F24F2F',
            }
          }
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: '#666666'
          },
          '& .MuiSelect-select': {
            color: '#000000'
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          color: '#000000',
          '&:hover': {
            borderColor: '#bdbdbd',
          },
          '&.Mui-focused': {
            borderColor: '#F24F2F',
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: "'ClashGrotesk', sans-serif",
          '&.MuiChip-colorSuccess': {
            backgroundColor: 'rgba(76, 175, 79, 0.15)',
            color: '#2e7d32',
            border: '1px solid rgba(76, 175, 79, 0.3)',
          },
          '&.MuiChip-colorPrimary': {
            backgroundColor: 'rgba(242, 79, 47, 0.15)',
            color: '#d94429',
            border: '1px solid rgba(242, 79, 47, 0.3)',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: 'rgba(156, 39, 176, 0.15)',
            color: '#7b1fa2',
            border: '1px solid rgba(156, 39, 176, 0.3)',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: 'rgba(245, 124, 0, 0.15)',
            color: '#e65100',
            border: '1px solid rgba(245, 124, 0, 0.3)',
          },
          '&.MuiChip-colorError': {
            backgroundColor: 'rgba(211, 47, 47, 0.15)',
            color: '#c62828',
            border: '1px solid rgba(211, 47, 47, 0.3)',
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: 'none',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          color: '#000000',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: "'ClashGrotesk', sans-serif",
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: '#F24F2F',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#d94429',
          },
        },
        outlined: {
          borderColor: '#e0e0e0',
          color: '#000000',
          '&:hover': {
            borderColor: '#bdbdbd',
            backgroundColor: '#f5f5f5',
          },
        },
        text: {
          color: '#000000',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#000000',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e0e0e0',
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: '#000000',
          borderBottom: '1px solid #e0e0e0',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f5f5f5',
          color: '#000000',
        },
      },
    },
  },
  typography: {
    fontFamily: "'ClashGrotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 16, // Increased base font size by 2pt (was 14)
    h1: {
      fontFamily: "'ClashGrotesk', sans-serif",
      fontWeight: 700,
      color: '#000000',
      fontSize: '2.5rem', // Increased
    },
    h2: {
      fontFamily: "'ClashGrotesk', sans-serif",
      fontWeight: 600,
      color: '#000000',
      fontSize: '2.125rem', // Increased
    },
    h3: {
      fontFamily: "'ClashGrotesk', sans-serif",
      fontWeight: 600,
      color: '#000000',
      fontSize: '1.875rem', // Increased
    },
    h4: {
      fontFamily: "'ClashGrotesk', sans-serif",
      fontWeight: 600,
      color: '#000000',
      fontSize: '1.625rem', // Increased
    },
    h5: {
      fontFamily: "'ClashGrotesk', sans-serif",
      fontWeight: 600,
      color: '#000000',
      fontSize: '1.475rem', // Increased
    },
    h6: {
      fontFamily: "'ClashGrotesk', sans-serif",
      fontWeight: 600,
      color: '#000000',
      fontSize: '1.30rem', // Increased
    },
    body1: {
      color: '#000000',
      fontSize: '1.25rem', // Increased (16px)
    },
    body2: {
      color: '#666666',
      fontSize: '1.0rem', // Increased (14px)
    },
    button: {
      fontFamily: "'ClashGrotesk', sans-serif",
      fontWeight: 600,
      fontSize: '0.9375rem', // Increased (15px)
    },
  },
});

export const alertStyles = {
  info: {
    backgroundColor: 'rgba(242, 79, 47, 0.1)',
    border: '1px solid rgba(242, 79, 47, 0.3)',
    '& .MuiAlert-message': { 
      color: '#d94429',
      fontWeight: 600 
    },
    '& .MuiAlert-icon': { 
      color: '#d94429' 
    }
  },
  success: {
    backgroundColor: 'rgba(76, 175, 79, 0.1)',
    border: '1px solid rgba(76, 175, 79, 0.3)',
    '& .MuiAlert-message': { 
      color: '#2e7d32',
      fontWeight: 600 
    },
    '& .MuiAlert-icon': { 
      color: '#2e7d32' 
    }
  },
  warning: {
    backgroundColor: 'rgba(245, 124, 0, 0.1)',
    border: '1px solid rgba(245, 124, 0, 0.3)',
    '& .MuiAlert-message': { 
      color: '#e65100',
      fontWeight: 600 
    },
    '& .MuiAlert-icon': { 
      color: '#e65100' 
    }
  },
  error: {
    backgroundColor: 'rgba(211, 47, 47, 0.1)',
    border: '1px solid rgba(211, 47, 47, 0.3)',
    '& .MuiAlert-message': { 
      color: '#c62828',
      fontWeight: 600 
    },
    '& .MuiAlert-icon': { 
      color: '#c62828' 
    }
  }
};

// Export as glassTheme for backward compatibility
export const glassTheme = cleanTheme;